// src/components/resources/guides/GuideFilters.tsx

'use client'

import { useState, useEffect, useDeferredValue, useMemo } from 'react'
import RiFilterLine from '~/icons/remix/generated/RiFilterLine'
import type { BaseGuide } from '~/lib/resources/types/guides'
import type { DifficultyLevel } from '~/lib/resources/types/shared/enums'

interface GuideFiltersProps {
  guides: BaseGuide[]
  onFilter: (filtered: BaseGuide[]) => void
}

export function GuideFilters({ guides, onFilter }: GuideFiltersProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedLevel, setSelectedLevel] = useState<DifficultyLevel | null>(null)

  // Get unique categories and levels
  const categories = useMemo(
    () => Array.from(new Set(guides.map(g => g.category).filter((c): c is string => Boolean(c)))),
    [guides]
  )
  const levels = useMemo(
    () => Array.from(new Set(guides.map(g => g.difficulty).filter((d): d is DifficultyLevel => Boolean(d)))),
    [guides]
  )

  // Pre-build a per-guide lowercased searchable haystack so each keystroke is
  // O(n) substring checks against an already-flattened string.
  const searchableGuides = useMemo(
    () =>
      guides.map(guide => {
        const sectionStrings =
          'sections' in guide && Array.isArray(guide.sections)
            ? guide.sections.flatMap(section => [section.title, section.description ?? ''])
            : []
        return {
          guide,
          haystack: [
            guide.title,
            guide.description,
            guide.category ?? '',
            ...(guide.metadata?.keywords ?? []),
            ...sectionStrings
          ]
            .join(' ')
            .toLowerCase()
        }
      }),
    [guides]
  )

  // Defer the input so keystrokes commit immediately while the filter pass
  // runs at lower priority — this is the main mobile INP win here.
  const deferredQuery = useDeferredValue(searchQuery)

  const filteredGuides = useMemo(() => {
    const query = deferredQuery.trim().toLowerCase()
    return searchableGuides
      .filter(({ guide, haystack }) => {
        if (query && !haystack.includes(query)) return false
        if (selectedCategory && guide.category !== selectedCategory) return false
        if (selectedLevel && guide.difficulty !== selectedLevel) return false
        return true
      })
      .map(({ guide }) => guide)
  }, [searchableGuides, deferredQuery, selectedCategory, selectedLevel])

  useEffect(() => {
    onFilter(filteredGuides)
  }, [filteredGuides, onFilter])

  return (
    <div className="mb-8 space-y-4">
      {/* Search */}
      <div className="relative">
        <RiFilterLine className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          placeholder="Search guides..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white py-3 pl-12 pr-4 text-sm text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
          type="text"
        />
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-4">
        {/* Category Filter */}
        <div className="flex items-center gap-2">
          <RiFilterLine className="h-5 w-5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCategory(null)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-[transform,background-color] motion-safe:hover:scale-[1.05] motion-safe:active:scale-[0.95] ${
              !selectedCategory
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            All Categories
          </button>
          {categories.map(category => (
            <button
              type="button"
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-all hover:scale-[1.05] active:scale-[0.95] ${
                selectedCategory === category
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Level Filter */}
        {levels.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedLevel(null)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-[transform,background-color] motion-safe:hover:scale-[1.05] motion-safe:active:scale-[0.95] ${
                !selectedLevel
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              All Levels
            </button>
            {levels.map(level => (
              <button
                type="button"
                key={level}
                onClick={() => setSelectedLevel(level)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-all hover:scale-[1.05] active:scale-[0.95] ${
                  selectedLevel === level
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
} 